import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, CarFront, MapPinned, BadgeDollarSign, LogOut, PlusCircle, IdCard, History, TicketPercent, Wallet, Receipt, Radio, MessageCircle, Bell } from "lucide-react";
import { ModeSwitcher } from "@/components/ModeSwitcher";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { NotificationBell } from "@/components/NotificationBell";

const LINKS = [
  { to: "/admin", label: "Dispatch", icon: LayoutDashboard, end: true, id: "overview" },
  { to: "/admin/book", label: "Quick book", icon: PlusCircle, id: "book" },
  { to: "/admin/live", label: "Live rides", icon: Radio, id: "live" },
  { to: "/admin/messages", label: "Messages", icon: MessageCircle, id: "messages" },
  { to: "/admin/notifications", label: "Notifications", icon: Bell, id: "notifications" },
  { to: "/admin/rides", label: "Ride history", icon: CarFront, id: "rides" },
  { to: "/admin/drivers", label: "Drivers", icon: IdCard, id: "drivers" },
  { to: "/admin/users", label: "Users", icon: Users, id: "users" },
  { to: "/admin/directory", label: "Directory", icon: MapPinned, id: "directory" },
  { to: "/admin/pricing", label: "Pricing", icon: BadgeDollarSign, id: "pricing" },
  { to: "/admin/discounts", label: "Discount codes", icon: TicketPercent, id: "discounts" },
  { to: "/admin/payments", label: "Payments", icon: Receipt, id: "payments" },
  { to: "/admin/credit", label: "Ride Credit", icon: Wallet, id: "credit" },
  { to: "/admin/audit", label: "Audit", icon: History, id: "audit" },
];

const linkCls = ({ isActive }) =>
  `flex shrink-0 items-center gap-3 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
    isActive ? "bg-primary text-primary-foreground shadow-[0_0_16px_hsl(var(--primary)/0.45)]" : "text-muted-foreground hover:bg-muted hover:text-foreground"
  }`;

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen md:flex" data-testid="admin-layout">
      <aside className="border-b bg-card md:sticky md:top-0 md:h-screen md:w-64 md:shrink-0 md:border-b-0 md:border-r">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 md:flex-col md:items-start md:p-5">
          <Brand to="/admin" />
          <div className="flex items-center gap-2">
            <ModeSwitcher />
            <div className="md:hidden"><NotificationBell /></div>
          </div>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 md:flex-col md:px-3">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={linkCls} data-testid={`admin-nav-${l.id}`}>
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden px-5 md:absolute md:bottom-5 md:block md:w-60">
          <p className="truncate text-sm font-semibold">{user?.name}</p>
          <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
          <button onClick={onLogout} data-testid="admin-logout-button" className="mt-3 flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 p-4 md:p-8">
        <div className="mb-6 hidden justify-end md:flex"><NotificationBell /></div>
        <Outlet />
        <button onClick={onLogout} data-testid="admin-logout-button-mobile" className="mt-10 flex items-center gap-2 text-sm font-semibold text-muted-foreground md:hidden">
          <LogOut className="h-4 w-4" /> Log out
        </button>
      </main>
    </div>
  );
}

export const PageHead = ({ title, sub, children }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
    <div>
      <h1 className="font-display text-3xl font-bold tracking-tight">{title}</h1>
      {sub && <p className="mt-1 text-sm text-muted-foreground">{sub}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);
